import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Settings, DollarSign, Loader2, Save, ArrowRight, CheckCircle, Eye, FileCheck, Shield, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const ExpenseSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [offices, setOffices] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [thresholds, setThresholds] = useState({
    reviewer_cap: 500000,
    approver_cap: 1000000,
    currency: 'TZS'
  });
  const [previewAmount, setPreviewAmount] = useState(750000);
  const [previewResult, setPreviewResult] = useState(null);
  
  const { getAuthHeaders } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch offices
  useEffect(() => {
    const fetchOffices = async () => {
      try {
        const response = await fetch(`${API_URL}/api/offices/`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setOffices(data || []);
          if (data && data.length > 0) {
            setSelectedOffice(data[0].id);
          }
        } else {
          console.error('Failed to fetch offices:', response.status);
        }
      } catch (error) {
        console.error('Error fetching offices:', error);
        toast.error('Network error loading offices');
      } finally {
        setLoading(false);
      }
    };
    fetchOffices();
  }, []);

  // Fetch thresholds when office changes
  useEffect(() => {
    if (selectedOffice) {
      fetchThresholds(selectedOffice);
    }
  }, [selectedOffice]);

  // Update preview when amount or thresholds change
  useEffect(() => {
    if (selectedOffice && previewAmount > 0) {
      fetchPreview();
    }
  }, [previewAmount, thresholds]);

  const fetchThresholds = async (officeId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/offices/${officeId}/expense-thresholds`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setThresholds(data.thresholds);
      }
    } catch (error) {
      console.error('Error fetching thresholds:', error);
    }
  };

  const fetchPreview = async () => {
    if (!selectedOffice) return;
    try {
      const response = await fetch(
        `${API_URL}/api/admin/offices/${selectedOffice}/expense-thresholds/preview?amount=${previewAmount}`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setPreviewResult(data);
      }
    } catch (error) {
      console.error('Error fetching preview:', error);
    }
  };

  const handleSave = async () => {
    if (!selectedOffice) return;
    
    // Validate
    if (thresholds.reviewer_cap >= thresholds.approver_cap) {
      toast.error('Reviewer cap must be less than Approver cap');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/offices/${selectedOffice}/expense-thresholds`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          reviewer_cap: thresholds.reviewer_cap,
          approver_cap: thresholds.approver_cap
        })
      });
      
      if (response.ok) {
        toast.success('Expense thresholds saved successfully');
        fetchThresholds(selectedOffice);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save thresholds');
      }
    } catch (error) {
      console.error('Error saving thresholds:', error);
      toast.error('Failed to save thresholds');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US').format(amount);
  };

  const selectedOfficeName = offices.find(o => o.id === selectedOffice)?.name || '';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="expense-thresholds-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Settings className="h-6 w-6" />
            Expense Approval Caps
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Set maximum amounts each approval level can authorize
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving || !selectedOffice} data-testid="save-thresholds-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      {/* How it works */}
      <Alert className="bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-700">
          <strong>How approval caps work:</strong>
          <ul className="list-disc ml-5 mt-2 space-y-1 text-sm">
            <li><strong>All expenses</strong> must go through <span className="text-blue-600 font-medium">Review</span> first</li>
            <li>If amount ≤ <strong>Reviewer Cap</strong>: Reviewer approves → Disbursement</li>
            <li>If amount &gt; Reviewer Cap but ≤ <strong>Approver Cap</strong>: Reviewer → <span className="text-amber-600 font-medium">Approver</span> → Disbursement</li>
            <li>If amount &gt; Approver Cap: Reviewer → Approver → <span className="text-red-600 font-medium">Authorizer</span> → Disbursement</li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* Office Selector */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Select Office</CardTitle>
        </CardHeader>
        <CardContent>
          {offices.length === 0 ? (
            <Alert className="bg-yellow-50 border-yellow-200">
              <AlertDescription className="text-yellow-700">
                No offices found. Please create an office first in <strong>Manage Offices</strong> before configuring expense thresholds.
              </AlertDescription>
            </Alert>
          ) : (
            <Select value={selectedOffice} onValueChange={setSelectedOffice}>
              <SelectTrigger className="w-full" data-testid="office-selector">
                <SelectValue placeholder="Select an office" />
              </SelectTrigger>
              <SelectContent>
                {offices.map(office => (
                  <SelectItem key={office.id} value={office.id}>
                    {office.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </CardContent>
      </Card>

      {selectedOffice && (
        <>
          {/* Two Cap Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Reviewer Cap */}
            <Card className="border-blue-200 bg-blue-50/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-blue-700">
                  <Eye className="h-5 w-5" />
                  Reviewer Cap
                </CardTitle>
                <CardDescription className="text-blue-600 text-xs">
                  Maximum amount a reviewer can approve alone
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label className="text-blue-700">Cap Amount ({thresholds.currency})</Label>
                  <Input
                    type="number"
                    value={thresholds.reviewer_cap}
                    onChange={(e) => setThresholds({
                      ...thresholds,
                      reviewer_cap: parseFloat(e.target.value) || 0
                    })}
                    className="border-blue-300 focus:ring-blue-500"
                    data-testid="reviewer-cap-input"
                  />
                  <p className="text-xs text-blue-600">
                    Amounts ≤ {formatCurrency(thresholds.reviewer_cap)} → Reviewer only
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Approver Cap */}
            <Card className="border-amber-200 bg-amber-50/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-amber-700">
                  <FileCheck className="h-5 w-5" />
                  Approver Cap
                </CardTitle>
                <CardDescription className="text-amber-600 text-xs">
                  Maximum amount an approver can authorize
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label className="text-amber-700">Cap Amount ({thresholds.currency})</Label>
                  <Input
                    type="number"
                    value={thresholds.approver_cap}
                    onChange={(e) => setThresholds({
                      ...thresholds,
                      approver_cap: parseFloat(e.target.value) || 0
                    })}
                    className="border-amber-300 focus:ring-amber-500"
                    data-testid="approver-cap-input"
                  />
                  <p className="text-xs text-amber-600">
                    Amounts &gt; {formatCurrency(thresholds.approver_cap)} → Needs Authorizer
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Validation Warning */}
          {thresholds.reviewer_cap >= thresholds.approver_cap && (
            <Alert className="bg-red-50 border-red-300">
              <AlertDescription className="text-red-700">
                <strong>⚠️ Invalid configuration:</strong> Reviewer cap ({formatCurrency(thresholds.reviewer_cap)}) must be less than Approver cap ({formatCurrency(thresholds.approver_cap)})
              </AlertDescription>
            </Alert>
          )}

          {/* Workflow Preview */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-600" />
                Workflow Preview
              </CardTitle>
              <CardDescription>
                Test how different expense amounts flow through approval
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label>Test Amount ({thresholds.currency})</Label>
                  <Input
                    type="number"
                    value={previewAmount}
                    onChange={(e) => setPreviewAmount(parseFloat(e.target.value) || 0)}
                    className="mt-1"
                    data-testid="preview-amount-input"
                  />
                </div>
                <div className="text-lg font-semibold text-gray-600 mt-6">
                  = {formatCurrency(previewAmount)} {thresholds.currency}
                </div>
              </div>

              {previewResult && (
                <div className="p-4 bg-gray-50 rounded-lg border mt-4">
                  <p className="font-medium mb-3">Approval Workflow for {formatCurrency(previewAmount)} {thresholds.currency}:</p>
                  
                  <div className="flex items-center gap-2 flex-wrap mb-4">
                    <Badge className="bg-gray-600 text-white">Submit</Badge>
                    <ArrowRight className="h-4 w-4 text-gray-400" />
                    
                    <Badge className="bg-blue-600 text-white">
                      <Eye className="h-3 w-3 mr-1" />
                      Review
                    </Badge>
                    
                    {previewResult.requires_approval && (
                      <>
                        <ArrowRight className="h-4 w-4 text-gray-400" />
                        <Badge className="bg-amber-600 text-white">
                          <FileCheck className="h-3 w-3 mr-1" />
                          Approval
                        </Badge>
                      </>
                    )}
                    
                    {previewResult.requires_authorization && (
                      <>
                        <ArrowRight className="h-4 w-4 text-gray-400" />
                        <Badge className="bg-red-600 text-white">
                          <Shield className="h-3 w-3 mr-1" />
                          Authorization
                        </Badge>
                      </>
                    )}
                    
                    <ArrowRight className="h-4 w-4 text-gray-400" />
                    <Badge className="bg-green-600 text-white">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Disbursement
                    </Badge>
                  </div>
                  
                  {/* Explanation */}
                  <div className="p-3 bg-white rounded border text-sm">
                    {previewAmount <= thresholds.reviewer_cap ? (
                      <p className="text-blue-700">
                        <strong>✓ Within Reviewer Cap:</strong> Amount ({formatCurrency(previewAmount)}) ≤ Reviewer cap ({formatCurrency(thresholds.reviewer_cap)}). 
                        Reviewer can approve directly without forwarding.
                      </p>
                    ) : previewAmount <= thresholds.approver_cap ? (
                      <p className="text-amber-700">
                        <strong>→ Exceeds Reviewer Cap:</strong> Amount ({formatCurrency(previewAmount)}) &gt; Reviewer cap ({formatCurrency(thresholds.reviewer_cap)}).
                        Reviewer will forward to Approver for final approval.
                      </p>
                    ) : (
                      <p className="text-red-700">
                        <strong>→ Exceeds Approver Cap:</strong> Amount ({formatCurrency(previewAmount)}) &gt; Approver cap ({formatCurrency(thresholds.approver_cap)}).
                        Requires Authorizer for final approval.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Summary Info */}
          <Alert className="bg-gray-50 border-gray-200">
            <AlertDescription className="text-gray-700">
              <strong>Summary for {selectedOfficeName}:</strong>
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li>Expenses ≤ <strong>{formatCurrency(thresholds.reviewer_cap)}</strong> {thresholds.currency}: <span className="text-blue-600 font-medium">Review only</span></li>
                <li>Expenses {formatCurrency(thresholds.reviewer_cap)} - {formatCurrency(thresholds.approver_cap)} {thresholds.currency}: <span className="text-amber-600 font-medium">Review + Approval</span></li>
                <li>Expenses &gt; <strong>{formatCurrency(thresholds.approver_cap)}</strong> {thresholds.currency}: <span className="text-red-600 font-medium">Review + Approval + Authorization</span></li>
              </ul>
            </AlertDescription>
          </Alert>
        </>
      )}
    </div>
  );
};

export default ExpenseSettings;
